import { useState, useEffect, useMemo } from 'react'
import {
  ArrowLeft, FileBarChart, Download, Loader2, Calendar,
  Building, Filter, Users, ClipboardList, AlertTriangle, Euro
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchAllIn } from '../../lib/fetchAll'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'

interface Props {
  onBack: () => void
}

interface Center {
  id: string
  name: string
}

interface SessionRow {
  id: string
  periode: string
  semaine: string
  start_date: string
  end_date: string
  center_id: string
  nombre_jours: number
}

interface RegistrationRow {
  id: string
  kid_id: string
  activity_id: string
  price_type: string
  cancellation_status: string
}

interface PresenceRow {
  registration_id: string
  monday_present: boolean | null
  tuesday_present: boolean | null
  wednesday_present: boolean | null
  thursday_present: boolean | null
  friday_present: boolean | null
}

interface KidRow {
  id: string
  nom: string
  prenom: string
  date_naissance: string
}

interface InclusionRow {
  kid_id: string
  has_needs: boolean
}

interface WeekData {
  semaine: string
  startDate: string
  endDate: string
  days: DayData[]
}

interface DayData {
  date: string
  dayLabel: string
  isHoliday: boolean
  under6: CategoryCounts
  over5: CategoryCounts
}

interface CategoryCounts {
  valides: number
  defavorises: number
  handicap: number
  total: number
}

type ChildCategory = 'valide' | 'defavorise' | 'handicap'

interface ChildReportRow {
  kidId: string
  nom: string
  prenom: string
  ageAtPeriodStart: number
  totalPresences: number
  category: ChildCategory
  isUnder6: boolean
}

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven']

const ONE_BASE_RATE = 1.25
const ONE_SOCIAL_BONUS = 0.90
const ONE_HANDICAP_BONUS = 7.50
const ONE_SOCIAL_THRESHOLD = 0.30

interface SubsidyWeek {
  semaine: string
  startDate: string
  endDate: string
  distinctKids: number
  distinctDefavKids: number
  distinctHandiKids: number
  kidDaysTotal: number
  kidDaysDefav: number
  kidDaysHandi: number
  socialRatio: number
  thresholdMet: boolean
  baseAmount: number
  socialAmount: number
  handicapAmount: number
  totalAmount: number
}

const formatEuro = (n: number) =>
  n.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

export function ONEReportCDV({ onBack }: Props) {
  const [centers, setCenters] = useState<Center[]>([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [selectedCenter, setSelectedCenter] = useState('')
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([])
  const [availableYears, setAvailableYears] = useState<string[]>([])

  const [loading, setLoading] = useState(false)
  const [generated, setGenerated] = useState(false)
  const [activeTab, setActiveTab] = useState<'presences' | 'enfants'>('presences')

  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([])
  const [presences, setPresences] = useState<PresenceRow[]>([])
  const [kids, setKids] = useState<KidRow[]>([])
  const [inclusions, setInclusions] = useState<InclusionRow[]>([])
  const [holidays, setHolidays] = useState<string[]>([])

  useEffect(() => {
    const fetchCenters = async () => {
      const { data } = await supabase
        .from('centers')
        .select('id, name')
        .eq('active', true)
        .order('name')
      setCenters(data || [])
    }
    fetchCenters()
  }, [])

  useEffect(() => {
    const fetchYears = async () => {
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select('start_date'), await stagesDesJpa())
      if (data) {
        const years = [...new Set(data.map(s => new Date(s.start_date).getFullYear().toString()))].sort().reverse()
        setAvailableYears(years)
      }
    }
    fetchYears()
  }, [])

  useEffect(() => {
    setSelectedPeriod('')
    if (!selectedYear || !selectedCenter) { setAvailablePeriods([]); return }

    const fetchPeriods = async () => {
      const yearStart = `${selectedYear}-01-01`
      const yearEnd = `${selectedYear}-12-31`
      const { data } = await sansJpa(supabase
        .from('sessions')
        .select('periode')
        .eq('center_id', selectedCenter)
        .gte('start_date', yearStart)
        .lte('start_date', yearEnd), await stagesDesJpa())

      if (data) {
        const unique = [...new Set(data.map(s => s.periode))].sort()
        setAvailablePeriods(unique)
      }
    }
    fetchPeriods()
  }, [selectedYear, selectedCenter])

  const handleGenerate = async () => {
    if (!selectedYear || !selectedPeriod || !selectedCenter) return
    setLoading(true)
    setGenerated(false)

    try {
      const yearStart = `${selectedYear}-01-01`
      const yearEnd = `${selectedYear}-12-31`

      const { data: sessionsData } = await sansJpa(supabase
        .from('sessions')
        .select('id, periode, semaine, start_date, end_date, center_id, nombre_jours')
        .eq('center_id', selectedCenter)
        .eq('periode', selectedPeriod)
        .gte('start_date', yearStart)
        .lte('start_date', yearEnd)
        .order('start_date'), await stagesDesJpa())

      if (!sessionsData || sessionsData.length === 0) {
        setSessions([])
        setGenerated(true)
        setLoading(false)
        return
      }

      setSessions(sessionsData)
      const sessionIds = sessionsData.map(s => s.id)

      const regsData = await fetchAllIn<RegistrationRow>(
        'registrations',
        'id, kid_id, activity_id, price_type, cancellation_status',
        'activity_id',
        sessionIds,
      )
      setRegistrations(regsData)

      const regIds = regsData.map(r => r.id)
      const allPresences = await fetchAllIn<PresenceRow>(
        'presences',
        'registration_id, monday_present, tuesday_present, wednesday_present, thursday_present, friday_present',
        'registration_id',
        regIds,
      )
      setPresences(allPresences)

      const kidIds = [...new Set(regsData.map(r => r.kid_id))]
      const allKids = await fetchAllIn<KidRow>(
        'kids',
        'id, nom, prenom, date_naissance',
        'id',
        kidIds,
      )
      setKids(allKids)

      const allInclusions = await fetchAllIn<InclusionRow>(
        'kid_inclusion',
        'kid_id, has_needs',
        'kid_id',
        kidIds,
        (q: any) => q.eq('has_needs', true),
      )
      setInclusions(allInclusions)

      const minDate = sessionsData.reduce((min, s) => s.start_date < min ? s.start_date : min, sessionsData[0].start_date)
      const maxDate = sessionsData.reduce((max, s) => s.end_date > max ? s.end_date : max, sessionsData[0].end_date)

      const { data: holidaysData } = await supabase
        .from('public_holidays')
        .select('date')
        .gte('date', minDate)
        .lte('date', maxDate)
      setHolidays((holidaysData || []).map(h => h.date))

      setGenerated(true)
    } catch (err) {
      console.error('Error generating report:', err)
    } finally {
      setLoading(false)
    }
  }

  const periodStartDate = useMemo(() => {
    if (sessions.length === 0) return null
    return sessions.reduce((min, s) => s.start_date < min ? s.start_date : min, sessions[0].start_date)
  }, [sessions])

  const inclusionSet = useMemo(() => {
    return new Set(inclusions.map(i => i.kid_id))
  }, [inclusions])

  const kidSocialSet = useMemo(() => {
    const socialKids = new Set<string>()
    for (const reg of registrations) {
      const pt = (reg.price_type || '').trim()
      if (pt === 'reduced' || pt === 'local_reduced') {
        socialKids.add(reg.kid_id)
      }
    }
    return socialKids
  }, [registrations])

  const kidsMap = useMemo(() => {
    const map = new Map<string, KidRow>()
    for (const k of kids) map.set(k.id, k)
    return map
  }, [kids])

  const getAge = (birthDate: string, refDate: string): number => {
    const birth = new Date(birthDate)
    const ref = new Date(refDate)
    let age = ref.getFullYear() - birth.getFullYear()
    const m = ref.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) age--
    return age
  }

  const getChildCategory = (kidId: string): ChildCategory => {
    if (inclusionSet.has(kidId)) return 'handicap'
    if (kidSocialSet.has(kidId)) return 'defavorise'
    return 'valide'
  }

  const activeRegistrations = useMemo(() => {
    return registrations.filter(r =>
      r.cancellation_status === 'none' ||
      !r.cancellation_status ||
      r.cancellation_status === ''
    )
  }, [registrations])

  const presencesByRegId = useMemo(() => {
    const map = new Map<string, PresenceRow>()
    for (const p of presences) map.set(p.registration_id, p)
    return map
  }, [presences])

  const regsBySession = useMemo(() => {
    const map = new Map<string, RegistrationRow[]>()
    for (const r of activeRegistrations) {
      const list = map.get(r.activity_id) || []
      list.push(r)
      map.set(r.activity_id, list)
    }
    return map
  }, [activeRegistrations])

  const weekDataList: WeekData[] = useMemo(() => {
    if (!generated || sessions.length === 0 || !periodStartDate) return []

    const weekMap = new Map<string, SessionRow[]>()
    for (const s of sessions) {
      const list = weekMap.get(s.semaine) || []
      list.push(s)
      weekMap.set(s.semaine, list)
    }

    const weeks: WeekData[] = []
    const sortedWeeks = [...weekMap.keys()].sort()

    for (const sem of sortedWeeks) {
      const weekSessions = weekMap.get(sem)!
      const startDate = weekSessions.reduce((min, s) => s.start_date < min ? s.start_date : min, weekSessions[0].start_date)
      const endDate = weekSessions.reduce((max, s) => s.end_date > max ? s.end_date : max, weekSessions[0].end_date)

      const days: DayData[] = []
      const start = new Date(startDate)

      for (let i = 0; i < 5; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const dateStr = d.toISOString().split('T')[0]
        const isHoliday = holidays.includes(dateStr)

        const dayIndex = i
        const presentKidsUnder6 = new Set<string>()
        const presentKidsOver5 = new Set<string>()
        const presentKidsUnder6Cat = { valides: 0, defavorises: 0, handicap: 0 }
        const presentKidsOver5Cat = { valides: 0, defavorises: 0, handicap: 0 }

        if (!isHoliday) {
          const dayFields: (keyof PresenceRow)[] = [
            'monday_present', 'tuesday_present', 'wednesday_present',
            'thursday_present', 'friday_present'
          ]

          for (const session of weekSessions) {
            const regs = regsBySession.get(session.id) || []
            for (const reg of regs) {
              const pres = presencesByRegId.get(reg.id)
              if (!pres) continue
              const isPresent = pres[dayFields[dayIndex]]
              if (!isPresent) continue

              const kid = kidsMap.get(reg.kid_id)
              if (!kid) continue

              const age = getAge(kid.date_naissance, periodStartDate)
              const isUnder6 = age < 6
              const trackingSet = isUnder6 ? presentKidsUnder6 : presentKidsOver5

              if (trackingSet.has(reg.kid_id)) continue
              trackingSet.add(reg.kid_id)

              const cat = getChildCategory(reg.kid_id)
              const catCounts = isUnder6 ? presentKidsUnder6Cat : presentKidsOver5Cat
              if (cat === 'handicap') catCounts.handicap++
              else if (cat === 'defavorise') catCounts.defavorises++
              else catCounts.valides++
            }
          }
        }

        days.push({
          date: dateStr,
          dayLabel: DAY_LABELS[i],
          isHoliday,
          under6: {
            ...presentKidsUnder6Cat,
            total: presentKidsUnder6Cat.valides + presentKidsUnder6Cat.defavorises + presentKidsUnder6Cat.handicap
          },
          over5: {
            ...presentKidsOver5Cat,
            total: presentKidsOver5Cat.valides + presentKidsOver5Cat.defavorises + presentKidsOver5Cat.handicap
          }
        })
      }

      weeks.push({ semaine: sem, startDate, endDate, days })
    }

    return weeks
  }, [generated, sessions, periodStartDate, holidays, regsBySession, presencesByRegId, kidsMap, inclusionSet, kidSocialSet])

  const childReportRows: ChildReportRow[] = useMemo(() => {
    if (!generated || sessions.length === 0 || !periodStartDate) return []

    const kidPresenceDays = new Map<string, Set<string>>()

    const dayFields: (keyof PresenceRow)[] = [
      'monday_present', 'tuesday_present', 'wednesday_present',
      'thursday_present', 'friday_present'
    ]

    for (const session of sessions) {
      const regs = regsBySession.get(session.id) || []
      const start = new Date(session.start_date)

      for (const reg of regs) {
        const pres = presencesByRegId.get(reg.id)
        if (!pres) continue

        for (let i = 0; i < 5; i++) {
          if (!pres[dayFields[i]]) continue
          const d = new Date(start)
          d.setDate(start.getDate() + i)
          const dateStr = d.toISOString().split('T')[0]
          if (holidays.includes(dateStr)) continue

          const set = kidPresenceDays.get(reg.kid_id) || new Set<string>()
          set.add(dateStr)
          kidPresenceDays.set(reg.kid_id, set)
        }
      }
    }

    const rows: ChildReportRow[] = []
    const allKidIds = new Set<string>()
    for (const reg of activeRegistrations) allKidIds.add(reg.kid_id)

    for (const kidId of allKidIds) {
      const kid = kidsMap.get(kidId)
      if (!kid) continue

      const age = getAge(kid.date_naissance, periodStartDate)
      const totalPresences = kidPresenceDays.get(kidId)?.size || 0
      const category = getChildCategory(kidId)

      rows.push({
        kidId,
        nom: kid.nom,
        prenom: kid.prenom,
        ageAtPeriodStart: age,
        totalPresences,
        category,
        isUnder6: age < 6
      })
    }

    rows.sort((a, b) => a.nom.localeCompare(b.nom, 'fr') || a.prenom.localeCompare(b.prenom, 'fr'))
    return rows
  }, [generated, sessions, periodStartDate, activeRegistrations, regsBySession, presencesByRegId, kidsMap, holidays, inclusionSet, kidSocialSet])

  const subsidyWeeks: SubsidyWeek[] = useMemo(() => {
    if (!generated || sessions.length === 0) return []

    const weekMap = new Map<string, SessionRow[]>()
    for (const s of sessions) {
      const list = weekMap.get(s.semaine) || []
      list.push(s)
      weekMap.set(s.semaine, list)
    }

    const dayFields: (keyof PresenceRow)[] = [
      'monday_present', 'tuesday_present', 'wednesday_present',
      'thursday_present', 'friday_present'
    ]

    const rows: SubsidyWeek[] = []
    const sortedWeeks = [...weekMap.keys()].sort()

    for (const sem of sortedWeeks) {
      const weekSessions = weekMap.get(sem)!
      const startDate = weekSessions.reduce((min, s) => s.start_date < min ? s.start_date : min, weekSessions[0].start_date)
      const endDate = weekSessions.reduce((max, s) => s.end_date > max ? s.end_date : max, weekSessions[0].end_date)

      const kidsParticipating = new Set<string>()
      const kidsDefav = new Set<string>()
      const kidsHandi = new Set<string>()
      let kidDaysTotal = 0
      let kidDaysDefav = 0
      let kidDaysHandi = 0

      for (const session of weekSessions) {
        const regs = regsBySession.get(session.id) || []
        const start = new Date(session.start_date)

        for (const reg of regs) {
          const pres = presencesByRegId.get(reg.id)
          if (!pres) continue
          const cat = getChildCategory(reg.kid_id)

          for (let i = 0; i < 5; i++) {
            if (!pres[dayFields[i]]) continue
            const d = new Date(start)
            d.setDate(start.getDate() + i)
            const dateStr = d.toISOString().split('T')[0]
            if (holidays.includes(dateStr)) continue

            kidsParticipating.add(reg.kid_id)
            kidDaysTotal++
            if (cat === 'handicap') {
              kidsHandi.add(reg.kid_id)
              kidDaysHandi++
            } else if (cat === 'defavorise') {
              kidsDefav.add(reg.kid_id)
              kidDaysDefav++
            }
          }
        }
      }

      const distinctKids = kidsParticipating.size
      const socialRatio = distinctKids > 0 ? kidsDefav.size / distinctKids : 0
      const thresholdMet = socialRatio >= ONE_SOCIAL_THRESHOLD

      const baseAmount = kidDaysTotal * ONE_BASE_RATE
      const socialAmount = thresholdMet ? kidDaysDefav * ONE_SOCIAL_BONUS : 0
      const handicapAmount = kidDaysHandi * ONE_HANDICAP_BONUS
      const totalAmount = baseAmount + socialAmount + handicapAmount

      rows.push({
        semaine: sem, startDate, endDate,
        distinctKids,
        distinctDefavKids: kidsDefav.size,
        distinctHandiKids: kidsHandi.size,
        kidDaysTotal, kidDaysDefav, kidDaysHandi,
        socialRatio, thresholdMet,
        baseAmount, socialAmount, handicapAmount, totalAmount,
      })
    }

    return rows
  }, [generated, sessions, holidays, regsBySession, presencesByRegId, inclusionSet, kidSocialSet])

  const childrenUnder6 = useMemo(() => childReportRows.filter(r => r.isUnder6), [childReportRows])
  const childrenOver5 = useMemo(() => childReportRows.filter(r => !r.isUnder6), [childReportRows])

  const exportChildrenCSV = (rows: ChildReportRow[], label: string) => {
    const centerName = centers.find(c => c.id === selectedCenter)?.name || ''
    const header = 'N;Nom;Prenom;Age;Nbre de Journees;Milieu defavorise;Handicap'
    const lines = rows.map((r, i) => {
      return `${i + 1};${r.nom};${r.prenom};${r.ageAtPeriodStart};${r.totalPresences};${r.category === 'defavorise' ? 'Oui' : 'Non'};${r.category === 'handicap' ? 'Oui' : 'Non'}`
    })

    const totalPresences = rows.reduce((s, r) => s + r.totalPresences, 0)
    const totalDefav = rows.filter(r => r.category === 'defavorise').length
    const totalHandicap = rows.filter(r => r.category === 'handicap').length
    lines.push(`Total;${rows.length};;;${totalPresences};${totalDefav};${totalHandicap}`)

    const csv = [header, ...lines].join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ONE_CDV_${label}_${centerName}_${selectedPeriod}_${selectedYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportPresencesCSV = () => {
    const centerName = centers.find(c => c.id === selectedCenter)?.name || ''
    const lines: string[] = []

    for (const week of weekDataList) {
      lines.push(`${week.semaine} (${formatDate(week.startDate)} au ${formatDate(week.endDate)})`)
      const dayHeaders = week.days.map(d => d.isHoliday ? `${d.dayLabel} (ferie)` : `${d.dayLabel} ${formatDate(d.date)}`)
      lines.push(`Categorie;${dayHeaders.join(';')}`)

      lines.push(`Moins de 6 ans - Valides;${week.days.map(d => d.isHoliday ? '' : d.under6.valides).join(';')}`)
      lines.push(`Moins de 6 ans - Defavorises;${week.days.map(d => d.isHoliday ? '' : d.under6.defavorises).join(';')}`)
      lines.push(`Moins de 6 ans - Handicap;${week.days.map(d => d.isHoliday ? '' : d.under6.handicap).join(';')}`)
      lines.push(`Moins de 6 ans - Total;${week.days.map(d => d.isHoliday ? '' : d.under6.total).join(';')}`)
      lines.push(`6 ans et plus - Valides;${week.days.map(d => d.isHoliday ? '' : d.over5.valides).join(';')}`)
      lines.push(`6 ans et plus - Defavorises;${week.days.map(d => d.isHoliday ? '' : d.over5.defavorises).join(';')}`)
      lines.push(`6 ans et plus - Handicap;${week.days.map(d => d.isHoliday ? '' : d.over5.handicap).join(';')}`)
      lines.push(`6 ans et plus - Total;${week.days.map(d => d.isHoliday ? '' : d.over5.total).join(';')}`)
      lines.push(`TOTAL GENERAL;${week.days.map(d => d.isHoliday ? '' : d.under6.total + d.over5.total).join(';')}`)
      lines.push('')
    }

    const csv = lines.join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ONE_CDV_Presences_${centerName}_${selectedPeriod}_${selectedYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const printReport = () => {
    window.print()
  }

  const formatDate = (d: string) => {
    return new Date(d).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' })
  }

  const formatDateFull = (d: string) => {
    return new Date(d).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const centerName = centers.find(c => c.id === selectedCenter)?.name || ''

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4 print:hidden">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapport ONE - Centre de Vacances</h1>
          <p className="text-gray-600">Presences journalieres et listes d'enfants</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Calendar className="h-4 w-4" /> Annee
            </label>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Building className="h-4 w-4" /> Centre
            </label>
            <select
              value={selectedCenter}
              onChange={e => setSelectedCenter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selectionner</option>
              {centers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
              <Filter className="h-4 w-4" /> Periode
            </label>
            <select
              value={selectedPeriod}
              onChange={e => setSelectedPeriod(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={!selectedCenter}
            >
              <option value="">Selectionner</option>
              {availablePeriods.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleGenerate}
            disabled={!selectedYear || !selectedPeriod || !selectedCenter || loading}
            className={`flex items-center justify-center gap-2 px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedYear && selectedPeriod && selectedCenter && !loading
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Chargement...</>
            ) : (
              <><FileBarChart className="h-4 w-4" /> Generer</>
            )}
          </button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-xl font-bold">Rapport ONE - Centre de Vacances</h1>
        <p className="text-sm text-gray-600">{centerName} - {selectedPeriod} {selectedYear}</p>
      </div>

      {/* Results */}
      {generated && (
        <>
          {sessions.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-600">Aucune session trouvee pour cette selection.</p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1 print:hidden">
                <button
                  onClick={() => setActiveTab('presences')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'presences'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <ClipboardList className="h-4 w-4" />
                  Presences
                </button>
                <button
                  onClick={() => setActiveTab('enfants')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'enfants'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Users className="h-4 w-4" />
                  Enfants
                </button>
              </div>

              {/* Presences Tab */}
              {activeTab === 'presences' && (
                <div className="space-y-4">
                  <div className="flex justify-end gap-2 print:hidden">
                    <button
                      onClick={exportPresencesCSV}
                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Download className="h-4 w-4" /> CSV
                    </button>
                    <button
                      onClick={printReport}
                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Download className="h-4 w-4" /> Imprimer / PDF
                    </button>
                  </div>

                  {weekDataList.map(week => (
                    <div key={week.semaine} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
                      <div className="bg-blue-50 border-b border-blue-100 px-4 py-3">
                        <h3 className="text-sm font-semibold text-blue-900">
                          {week.semaine} - {formatDateFull(week.startDate)} au {formatDateFull(week.endDate)}
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left px-4 py-2 font-medium text-gray-700 min-w-[200px]">Categorie</th>
                              {week.days.map(day => (
                                <th
                                  key={day.date}
                                  className={`text-center px-3 py-2 font-medium min-w-[80px] ${
                                    day.isHoliday ? 'text-red-400 bg-red-50' : 'text-gray-700'
                                  }`}
                                >
                                  <div>{day.dayLabel}</div>
                                  <div className="text-xs font-normal">{formatDate(day.date)}</div>
                                  {day.isHoliday && <div className="text-xs font-normal text-red-400">Ferie</div>}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-sky-50/50">
                              <td colSpan={6} className="px-4 py-1.5 text-xs font-bold text-sky-800 uppercase tracking-wide">
                                Moins de 6 ans (30 mois - 5 ans)
                              </td>
                            </tr>
                            <PresenceRow label="Valides" days={week.days} getValue={d => d.under6.valides} />
                            <PresenceRow label="Defavorises" days={week.days} getValue={d => d.under6.defavorises} />
                            <PresenceRow label="Avec handicap" days={week.days} getValue={d => d.under6.handicap} />
                            <PresenceRow label="Total" days={week.days} getValue={d => d.under6.total} bold />

                            <tr className="bg-amber-50/50">
                              <td colSpan={6} className="px-4 py-1.5 text-xs font-bold text-amber-800 uppercase tracking-wide">
                                6 ans et plus
                              </td>
                            </tr>
                            <PresenceRow label="Valides" days={week.days} getValue={d => d.over5.valides} />
                            <PresenceRow label="Defavorises" days={week.days} getValue={d => d.over5.defavorises} />
                            <PresenceRow label="Avec handicap" days={week.days} getValue={d => d.over5.handicap} />
                            <PresenceRow label="Total" days={week.days} getValue={d => d.over5.total} bold />

                            <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                              <td className="px-4 py-2 text-gray-900">TOTAL GENERAL</td>
                              {week.days.map(day => (
                                <td key={day.date} className={`text-center px-3 py-2 ${day.isHoliday ? 'text-gray-300' : 'text-gray-900'}`}>
                                  {day.isHoliday ? '-' : day.under6.total + day.over5.total}
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}

                  {/* Period summary */}
                  <PeriodSummary weekDataList={weekDataList} />

                  {/* Subsidy estimate (screen only) */}
                  <SubsidyEstimate weeks={subsidyWeeks} />
                </div>
              )}

              {/* Enfants Tab */}
              {activeTab === 'enfants' && (
                <div className="space-y-6">
                  <ChildrenSummaryCards under6={childrenUnder6} over5={childrenOver5} />

                  <ChildrenTable
                    title="Moins de 6 ans (30 mois - 5 ans)"
                    rows={childrenUnder6}
                    onExportCSV={() => exportChildrenCSV(childrenUnder6, 'Moins_de_6_ans')}
                    colorClass="sky"
                  />
                  <ChildrenTable
                    title="6 ans et plus"
                    rows={childrenOver5}
                    onExportCSV={() => exportChildrenCSV(childrenOver5, '6_ans_et_plus')}
                    colorClass="amber"
                  />

                  <div className="flex justify-end print:hidden">
                    <button
                      onClick={printReport}
                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Download className="h-4 w-4" /> Imprimer / PDF
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

function PresenceRow({
  label, days, getValue, bold
}: {
  label: string
  days: DayData[]
  getValue: (d: DayData) => number
  bold?: boolean
}) {
  return (
    <tr className={`border-b border-gray-100 ${bold ? 'font-semibold bg-gray-50/50' : ''}`}>
      <td className="px-4 py-1.5 pl-8 text-gray-700">{label}</td>
      {days.map(day => (
        <td key={day.date} className={`text-center px-3 py-1.5 ${day.isHoliday ? 'text-gray-300' : 'text-gray-900'}`}>
          {day.isHoliday ? '-' : getValue(day) || ''}
        </td>
      ))}
    </tr>
  )
}

function PeriodSummary({ weekDataList }: { weekDataList: WeekData[] }) {
  if (weekDataList.length === 0) return null

  let totalDays = 0
  let totalKids = 0

  for (const week of weekDataList) {
    for (const day of week.days) {
      if (!day.isHoliday) {
        const dayTotal = day.under6.total + day.over5.total
        if (dayTotal > 0) {
          totalDays++
          totalKids += dayTotal
        }
      }
    }
  }

  const average = totalDays > 0 ? (totalKids / totalDays).toFixed(1) : '0'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:shadow-none print:border">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Recapitulatif de la periode</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{weekDataList.length}</p>
          <p className="text-xs text-blue-600 mt-1">Semaines</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{totalDays}</p>
          <p className="text-xs text-blue-600 mt-1">Jours d'activite</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{average}</p>
          <p className="text-xs text-blue-600 mt-1">Moyenne journaliere</p>
        </div>
      </div>
    </div>
  )
}

function ChildrenSummaryCards({
  under6, over5
}: {
  under6: ChildReportRow[]
  over5: ChildReportRow[]
}) {
  const statsU6 = {
    total: under6.length,
    valides: under6.filter(r => r.category === 'valide').length,
    defavorises: under6.filter(r => r.category === 'defavorise').length,
    handicap: under6.filter(r => r.category === 'handicap').length,
    totalPresences: under6.reduce((s, r) => s + r.totalPresences, 0),
  }
  const statsO5 = {
    total: over5.length,
    valides: over5.filter(r => r.category === 'valide').length,
    defavorises: over5.filter(r => r.category === 'defavorise').length,
    handicap: over5.filter(r => r.category === 'handicap').length,
    totalPresences: over5.reduce((s, r) => s + r.totalPresences, 0),
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SummaryCard title="Moins de 6 ans" stats={statsU6} colorClass="sky" />
      <SummaryCard title="6 ans et plus" stats={statsO5} colorClass="amber" />
    </div>
  )
}

function SummaryCard({
  title, stats, colorClass
}: {
  title: string
  stats: { total: number; valides: number; defavorises: number; handicap: number; totalPresences: number }
  colorClass: string
}) {
  const bgClass = colorClass === 'sky' ? 'bg-sky-50 border-sky-200' : 'bg-amber-50 border-amber-200'
  const textClass = colorClass === 'sky' ? 'text-sky-900' : 'text-amber-900'
  const subTextClass = colorClass === 'sky' ? 'text-sky-700' : 'text-amber-700'
  const numClass = colorClass === 'sky' ? 'text-sky-800' : 'text-amber-800'

  return (
    <div className={`rounded-xl border p-5 ${bgClass}`}>
      <h3 className={`text-sm font-bold ${textClass} mb-3`}>{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className={`text-2xl font-bold ${numClass}`}>{stats.total}</p>
          <p className={`text-xs ${subTextClass}`}>Enfants</p>
        </div>
        <div>
          <p className={`text-2xl font-bold ${numClass}`}>{stats.totalPresences}</p>
          <p className={`text-xs ${subTextClass}`}>Total journees</p>
        </div>
      </div>
      <div className="mt-3 flex gap-4 text-xs">
        <span className={subTextClass}>Valides: <strong>{stats.valides}</strong></span>
        <span className={subTextClass}>Defavorises: <strong>{stats.defavorises}</strong></span>
        <span className={subTextClass}>Handicap: <strong>{stats.handicap}</strong></span>
      </div>
    </div>
  )
}

function ChildrenTable({
  title, rows, onExportCSV, colorClass
}: {
  title: string
  rows: ChildReportRow[]
  onExportCSV: () => void
  colorClass: string
}) {
  const headerBg = colorClass === 'sky' ? 'bg-sky-50 border-sky-100' : 'bg-amber-50 border-amber-100'
  const headerText = colorClass === 'sky' ? 'text-sky-900' : 'text-amber-900'

  const totalPresences = rows.reduce((s, r) => s + r.totalPresences, 0)
  const totalDefav = rows.filter(r => r.category === 'defavorise').length
  const totalHandicap = rows.filter(r => r.category === 'handicap').length

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border print:break-inside-avoid">
      <div className={`${headerBg} border-b px-4 py-3 flex items-center justify-between`}>
        <h3 className={`text-sm font-semibold ${headerText}`}>
          {title} ({rows.length} enfants)
        </h3>
        <button
          onClick={onExportCSV}
          className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 transition-colors print:hidden"
        >
          <Download className="h-3.5 w-3.5" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-4 py-2 font-medium text-gray-600 w-12">N</th>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Nom</th>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Prenom</th>
              <th className="text-center px-4 py-2 font-medium text-gray-600">Age</th>
              <th className="text-center px-4 py-2 font-medium text-gray-600">Nbre de Journees</th>
              <th className="text-center px-4 py-2 font-medium text-gray-600">Milieu defavorise</th>
              <th className="text-center px-4 py-2 font-medium text-gray-600">Handicap</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400">Aucun enfant dans cette tranche d'age</td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.kidId} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2 text-gray-400 tabular-nums">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.nom}</td>
                  <td className="px-4 py-2 text-gray-700">{r.prenom}</td>
                  <td className="text-center px-4 py-2 text-gray-700">{r.ageAtPeriodStart}</td>
                  <td className="text-center px-4 py-2 text-gray-900 font-medium tabular-nums">{r.totalPresences}</td>
                  <td className="text-center px-4 py-2">
                    {r.category === 'defavorise' ? (
                      <span className="inline-block w-5 h-5 bg-orange-100 text-orange-700 rounded text-xs font-bold leading-5">O</span>
                    ) : ''}
                  </td>
                  <td className="text-center px-4 py-2">
                    {r.category === 'handicap' ? (
                      <span className="inline-block w-5 h-5 bg-purple-100 text-purple-700 rounded text-xs font-bold leading-5">O</span>
                    ) : ''}
                  </td>
                </tr>
              ))
            )}
            {rows.length > 0 && (
              <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                <td className="px-4 py-2 text-gray-900" colSpan={2}>Total</td>
                <td className="px-4 py-2 text-gray-700">{rows.length} enfants</td>
                <td className="text-center px-4 py-2"></td>
                <td className="text-center px-4 py-2 text-gray-900 tabular-nums">{totalPresences}</td>
                <td className="text-center px-4 py-2 text-gray-900 tabular-nums">{totalDefav}</td>
                <td className="text-center px-4 py-2 text-gray-900 tabular-nums">{totalHandicap}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SubsidyEstimate({ weeks }: { weeks: SubsidyWeek[] }) {
  if (weeks.length === 0) return null

  const totalBase = weeks.reduce((s, w) => s + w.baseAmount, 0)
  const totalSocial = weeks.reduce((s, w) => s + w.socialAmount, 0)
  const totalHandi = weeks.reduce((s, w) => s + w.handicapAmount, 0)
  const grandTotal = totalBase + totalSocial + totalHandi
  const totalKidDays = weeks.reduce((s, w) => s + w.kidDaysTotal, 0)
  const totalKidDaysDefavEligible = weeks
    .filter(w => w.thresholdMet)
    .reduce((s, w) => s + w.kidDaysDefav, 0)
  const totalKidDaysHandi = weeks.reduce((s, w) => s + w.kidDaysHandi, 0)

  const shortDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' })

  return (
    <div className="print:hidden bg-white rounded-xl shadow-sm border border-emerald-200 overflow-hidden">
      <div className="flex items-start justify-between gap-4 border-b border-emerald-100 bg-emerald-50 px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <Euro className="h-5 w-5 text-emerald-700" />
            <h3 className="text-sm font-semibold text-emerald-900">Estimation de la subvention ONE</h3>
          </div>
          <p className="text-xs text-emerald-700 mt-1">
            Base 1,25 € / journee-enfant &middot; Majoration socio-economique 0,90 € (si &ge; 30 % d'enfants defavorises la semaine) &middot; Complement handicap leger 7,50 €
          </p>
        </div>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 bg-white border border-emerald-200 rounded px-2 py-1">
          Interne - non inclus dans le PDF
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
              <th className="text-left px-4 py-2 font-medium">Semaine</th>
              <th className="text-center px-3 py-2 font-medium">Enfants</th>
              <th className="text-center px-3 py-2 font-medium">Defav. / Ratio</th>
              <th className="text-right px-3 py-2 font-medium">Base</th>
              <th className="text-right px-3 py-2 font-medium">Social</th>
              <th className="text-right px-3 py-2 font-medium">Handicap</th>
              <th className="text-right px-4 py-2 font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map(w => (
              <tr key={w.semaine} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-2 align-top">
                  <div className="font-medium text-gray-900">{w.semaine}</div>
                  <div className="text-xs text-gray-500">{shortDate(w.startDate)} - {shortDate(w.endDate)}</div>
                </td>
                <td className="text-center px-3 py-2 align-top text-gray-800 tabular-nums">{w.distinctKids}</td>
                <td className="text-center px-3 py-2 align-top">
                  <div className="text-gray-800 tabular-nums">{w.distinctDefavKids}</div>
                  <div className={`text-xs font-semibold tabular-nums ${w.thresholdMet ? 'text-emerald-700' : 'text-gray-400'}`}>
                    {(w.socialRatio * 100).toFixed(0)} %
                  </div>
                </td>
                <td className="text-right px-3 py-2 align-top tabular-nums text-gray-900">
                  <div>{formatEuro(w.baseAmount)}</div>
                  <div className="text-xs text-gray-500">{w.kidDaysTotal} j</div>
                </td>
                <td className="text-right px-3 py-2 align-top tabular-nums">
                  {w.thresholdMet ? (
                    <>
                      <div className="text-gray-900">{formatEuro(w.socialAmount)}</div>
                      <div className="text-xs text-gray-500">{w.kidDaysDefav} j</div>
                    </>
                  ) : (
                    <>
                      <div className="text-gray-400">{formatEuro(0)}</div>
                      <div className="text-xs text-gray-400">seuil non atteint</div>
                    </>
                  )}
                </td>
                <td className="text-right px-3 py-2 align-top tabular-nums text-gray-900">
                  <div>{formatEuro(w.handicapAmount)}</div>
                  <div className="text-xs text-gray-500">{w.kidDaysHandi} j</div>
                </td>
                <td className="text-right px-4 py-2 align-top font-semibold text-gray-900 tabular-nums">
                  {formatEuro(w.totalAmount)}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-emerald-200 bg-emerald-50/70">
              <td className="px-4 py-3 font-semibold text-emerald-900">Total periode</td>
              <td className="text-center px-3 py-3 text-gray-400 text-xs">-</td>
              <td className="text-center px-3 py-3 text-gray-400 text-xs">-</td>
              <td className="text-right px-3 py-3 tabular-nums text-gray-900 font-semibold">
                <div>{formatEuro(totalBase)}</div>
                <div className="text-xs font-normal text-gray-500">{totalKidDays} j</div>
              </td>
              <td className="text-right px-3 py-3 tabular-nums text-gray-900 font-semibold">
                <div>{formatEuro(totalSocial)}</div>
                <div className="text-xs font-normal text-gray-500">{totalKidDaysDefavEligible} j eligibles</div>
              </td>
              <td className="text-right px-3 py-3 tabular-nums text-gray-900 font-semibold">
                <div>{formatEuro(totalHandi)}</div>
                <div className="text-xs font-normal text-gray-500">{totalKidDaysHandi} j</div>
              </td>
              <td className="text-right px-4 py-3 text-emerald-800 text-base font-bold tabular-nums">
                {formatEuro(grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500 bg-gray-50/50">
        Le seuil de 30 % est evalue chaque semaine sur les enfants distincts participants (jours feries exclus).
        Un enfant classe a la fois "handicap" et "defavorise" est compte en handicap. Estimation indicative,
        les montants reels peuvent varier selon les regles de l'ONE.
      </div>
    </div>
  )
}
